/**
 * NEX-1645 (plan 2026-10-20, Tarea 3.4) — the copy of the two module states that close something
 * in this panel, kept in one place because each one is shown twice: as a screen (from
 * `GET /app/features`, `useStorageModuleState`) and as the text of the API's 503 when the state
 * changed after that read (`describeStorageError` in `storage-api.ts`, `describeStorageProfileError`
 * in `pages/empresa/storage-api.ts`). Plain strings, no transport, so both transports and the pages
 * may import it without crossing the coupling contract (root `AGENTS.md`).
 */

/** `off` — Login, Registro and the shell show it instead of the panel; also 503 `STORAGE_MODULE_DISABLED`. */
export const STORAGE_MODULE_OFF_MESSAGE = 'El módulo de depósitos todavía no está disponible. Te avisamos cuando abra.';

/** `registration_only` — Solicitudes shows it instead of offering Cotizar. */
export const STORAGE_REQUESTS_CLOSED_NOTICE =
  'Los clientes todavía no pueden solicitar depósitos. Mientras tanto completá tu alta y tus depósitos.';

/** 503 `STORAGE_REQUESTS_DISABLED` — a quote sent while the module is `registration_only`. */
export const STORAGE_REQUESTS_DISABLED_MESSAGE =
  'Todavía no se puede cotizar: los clientes aún no pueden solicitar depósitos. Mientras tanto completá tu alta y tus depósitos.';

/**
 * Audit CTR-3 (2026-10-30) — the module turned off from the backoffice while the panel was open.
 * `useStorageModuleState` keeps the state it read at load, so until now each page showed its own
 * generic error for the API's 503 `STORAGE_MODULE_DISABLED`. The four transports report that
 * answer here (`reportStorageModuleRefusal`, right before throwing their `ApiError` as always) and
 * the hook, subscribed, flips the kept state to `off`: the shell shows `StorageModuleOffPage`.
 * Stateless on purpose: nothing is kept here, the hook owns the state; a listener-less call (a
 * page test rendering without the shell) does nothing. Still plain, still no transport.
 */
type ModuleOffListener = () => void;

const moduleOffListeners = new Set<ModuleOffListener>();

/** Called by `useStorageModuleState`; returns the unsubscribe for the effect's cleanup. */
export function subscribeStorageModuleOff(listener: ModuleOffListener): () => void {
  moduleOffListeners.add(listener);
  return () => {
    moduleOffListeners.delete(listener);
  };
}

export function markStorageModuleOff(): void {
  moduleOffListeners.forEach((listener) => listener());
}

/** A transport's non-2xx answer: only the module's own 503 counts, never a gateway's 503 without the code. */
export function reportStorageModuleRefusal(status: number, code: string | undefined): void {
  if (status === 503 && code === 'STORAGE_MODULE_DISABLED') markStorageModuleOff();
}
